import { Card, CardContent } from "@/components/ui/card";
import { RankingPositionData, RankingItem } from "@/types/dashboard";
import { Trophy, Crown, Filter, Users, Medal, TrendingUp, User, Download } from "lucide-react";
import { exportRankingPdf } from "@/Api/dashboard";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Mask } from "@/utils/mask";
import { useState } from "react";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";

interface SellersRankingCardProps {
  data: RankingPositionData;
  goalType?: "meta" | "super_meta";
  currentSellerId?: number;
}

type ViewMode = "day" | "month";

export function SellersRankingCard({ data, goalType = "meta", currentSellerId }: SellersRankingCardProps) {
  const goalLabel = goalType === "super_meta" ? "Super-Meta" : "Meta";
  // Only highlight if currentSellerId is provided (seller viewing their own ranking)
  // Don't highlight for admins (they don't have a seller_id)
  const highlightedSellerId = currentSellerId || null;
  const [viewMode, setViewMode] = useState<ViewMode>("day");
  const [filterHitGoal, setFilterHitGoal] = useState(false);
  const [filterOnlyMe, setFilterOnlyMe] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const { toast } = useToast();

  const handleExportPdf = async () => {
    setIsExporting(true);
    try {
      const blob = await exportRankingPdf({
        ranking: filteredAllRanking,
        totalSellers: filteredAllRanking.length,
        hitGoalCount: hitGoalCount,
        viewMode: viewMode,
        filterHitGoal: filterHitGoal,
        goalLabel: goalLabel,
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `ranking-vendedores-${viewMode === "day" ? "dia" : "mes"}-${new Date().toISOString().split("T")[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast({
        description: "PDF exportado com sucesso!",
      });
    } catch {
      toast({
        description: "Erro ao exportar PDF",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  const getMedalColor = (index: number) => {
    if (index === 0) return "from-yellow-400 to-yellow-600 shadow-yellow-500/30";
    if (index === 1) return "from-gray-300 to-gray-500 shadow-gray-400/30";
    if (index === 2) return "from-amber-500 to-amber-700 shadow-amber-500/30";
    return "from-slate-200 to-slate-400 dark:from-slate-600 dark:to-slate-800 shadow-slate-400/20";
  };

  const getMedalIcon = (index: number) => {
    if (index === 0) return <Crown className="h-4 w-4 text-yellow-900" />;
    if (index === 1) return <Medal className="h-4 w-4 text-gray-700" />;
    if (index === 2) return <Medal className="h-4 w-4 text-amber-900" />;
    return <span className="text-xs font-bold text-slate-600 dark:text-slate-300">{index + 1}</span>;
  };

  // Selecionar ranking baseado no modo de visualização
  const rawRanking: RankingItem[] = viewMode === "day"
    ? (data.today.ranking || [])
    : (data.month.ranking || []);

  // Ranking completo para o modal
  const allRanking: RankingItem[] = viewMode === "day"
    ? (data.today.all_ranking || [])
    : (data.month.all_ranking || []);

  // Filtrar todos que bateram a meta (do ranking completo)
  const allHitGoal = allRanking.filter((seller) =>
    viewMode === "day" ? seller.hit_daily_goal : seller.hit_monthly_goal
  );

  // Encontrar posição do vendedor atual no ranking completo
  const currentSellerPosition = highlightedSellerId
    ? allRanking.findIndex((s) => s.seller_id === highlightedSellerId) + 1
    : null;

  // Quando filtro de meta está ativo, mostrar TODOS que bateram meta (não apenas top 10)
  // Quando filtro "apenas eu" está ativo, mostrar apenas o vendedor logado
  let ranking = rawRanking;
  if (filterOnlyMe && highlightedSellerId) {
    ranking = allRanking.filter((s) => s.seller_id === highlightedSellerId);
  } else if (filterHitGoal) {
    ranking = allHitGoal;
  }

  const filteredAllRanking = filterHitGoal ? allHitGoal : allRanking;

  const hitGoalCount = allHitGoal.length;

  const totalSellers = viewMode === "day" ? data.today.total : data.month.total;

  const renderSellerItem = (seller: RankingItem, index: number, realPosition?: number) => {
    const hitGoal = viewMode === "day" ? seller.hit_daily_goal : seller.hit_monthly_goal;
    const goal = viewMode === "day" ? seller.daily_goal : seller.monthly_goal;
    const isCurrentSeller = Boolean(highlightedSellerId) && highlightedSellerId === seller.seller_id;
    // Use real position if provided (for "only me" filter), otherwise use index
    const displayPosition = realPosition ?? index;

    return (
      <div
        key={seller.seller_id}
        className={cn(
          "flex items-center gap-2 p-2.5 rounded-xl",
          isCurrentSeller
            ? "bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-400 dark:border-blue-600 ring-2 ring-blue-200 dark:ring-blue-800"
            : hitGoal
              ? "bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800"
              : "bg-gray-50 dark:bg-gray-800/50"
        )}
      >
        <div
          className={cn(
            "w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0",
            displayPosition === 0 ? "bg-yellow-400 text-yellow-900" :
            displayPosition === 1 ? "bg-gray-300 text-gray-700" :
            displayPosition === 2 ? "bg-amber-400 text-amber-900" :
            "bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400"
          )}
        >
          {displayPosition === 0 ? <Crown className="h-3 w-3" /> : <span className="text-[10px] font-bold">{displayPosition + 1}</span>}
        </div>
        <div className="flex-1 min-w-0 overflow-hidden">
          <div className="flex items-center gap-1">
            <p className="text-sm font-medium text-gray-900 dark:text-white leading-tight truncate" title={seller.seller_name}>
              {seller.seller_name}
            </p>
            {isCurrentSeller && (
              <span className="flex-shrink-0 px-1.5 py-0.5 text-[9px] font-bold bg-blue-500 text-white rounded">
                VOCÊ
              </span>
            )}
            {hitGoal && (
              <span className="flex-shrink-0 px-1.5 py-0.5 text-[9px] font-bold bg-green-500 text-white rounded">
                {goalLabel.toUpperCase()}!
              </span>
            )}
          </div>
          <p className="text-[10px] text-gray-500 dark:text-gray-400 leading-tight">
            {seller.total_orders} vendas
          </p>
        </div>
        <div className="text-right flex-shrink-0">
          <p className="text-sm font-bold text-emerald-600 dark:text-emerald-400">
            {Mask.formatCurrency(seller.total_sales)}
          </p>
          {goal && goal > 0 && (
            <p className="text-[9px] text-gray-400">
              {Math.round((seller.total_sales / goal) * 100)}%
            </p>
          )}
        </div>
      </div>
    );
  };

  const renderModalSellerItem = (seller: RankingItem, index: number) => {
    const hitGoal = viewMode === "day" ? seller.hit_daily_goal : seller.hit_monthly_goal;
    const goal = viewMode === "day" ? seller.daily_goal : seller.monthly_goal;
    const percentage = goal && goal > 0 ? Math.min((seller.total_sales / goal) * 100, 100) : 0;
    const displayPercentage = goal && goal > 0 ? Math.round((seller.total_sales / goal) * 100) : 0;
    const isCurrentSeller = Boolean(highlightedSellerId) && highlightedSellerId === seller.seller_id;

    return (
      <div
        key={seller.seller_id}
        className={cn(
          "relative overflow-hidden rounded-xl transition-all duration-200",
          isCurrentSeller
            ? "bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 border-2 border-blue-400 dark:border-blue-600 shadow-lg shadow-blue-500/20 ring-2 ring-blue-200 dark:ring-blue-800"
            : hitGoal
              ? "bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30 border-2 border-green-300 dark:border-green-700 shadow-lg shadow-green-500/10"
              : "bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:shadow-md"
        )}
      >
        <div className="p-4">
          <div className="flex items-center gap-4">
            {/* Medalha/Posição */}
            <div
              className={cn(
                "w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 bg-gradient-to-br shadow-lg",
                getMedalColor(index)
              )}
            >
              {getMedalIcon(index)}
            </div>

            {/* Info do vendedor */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-semibold text-gray-900 dark:text-white truncate">
                  {seller.seller_name}
                </h3>
                {isCurrentSeller && (
                  <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-bold bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-full shadow-sm">
                    VOCÊ
                  </span>
                )}
                {hitGoal && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-full shadow-sm">
                    <TrendingUp className="h-3 w-3" />
                    {goalLabel.toUpperCase()} {viewMode === "day" ? "DIA" : "MÊS"} BATIDA!
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                {seller.total_orders} vendas realizadas
              </p>
            </div>

            {/* Valores */}
            <div className="text-right flex-shrink-0">
              <p className={cn(
                "text-lg font-bold",
                hitGoal
                  ? "text-green-600 dark:text-green-400"
                  : "text-gray-900 dark:text-white"
              )}>
                {Mask.formatCurrency(seller.total_sales)}
              </p>
              {goal && goal > 0 && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {goalLabel}: {Mask.formatCurrency(goal)}
                </p>
              )}
            </div>
          </div>

          {/* Barra de progresso */}
          {goal && goal > 0 && (
            <div className="mt-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-gray-500 dark:text-gray-400">Progresso da {goalLabel.toLowerCase()}</span>
                <span className={cn(
                  "text-xs font-semibold",
                  displayPercentage >= 100 ? "text-green-600 dark:text-green-400" : "text-gray-600 dark:text-gray-300"
                )}>
                  {displayPercentage}%
                </span>
              </div>
              <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className={cn(
                    "h-full rounded-full transition-all duration-500",
                    displayPercentage >= 100
                      ? "bg-gradient-to-r from-green-500 to-emerald-500"
                      : displayPercentage >= 80
                      ? "bg-gradient-to-r from-yellow-500 to-amber-500"
                      : "bg-gradient-to-r from-blue-500 to-cyan-500"
                  )}
                  style={{ width: `${Math.min(percentage, 100)}%` }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Indicador de posição no canto esquerdo */}
        {index < 3 && (
          <div className="absolute top-0 left-0 w-16 h-16 overflow-hidden">
            <div className={cn(
              "absolute transform -rotate-45 text-[10px] font-bold text-center w-20 py-0.5 top-3 -left-5",
              index === 0 && "bg-yellow-500 text-yellow-900",
              index === 1 && "bg-gray-400 text-gray-800",
              index === 2 && "bg-amber-500 text-amber-900"
            )}>
              {index === 0 && "1º"}
              {index === 1 && "2º"}
              {index === 2 && "3º"}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <Card className="h-full border-0 shadow-lg bg-white dark:bg-gray-900">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                <Trophy className="h-4 w-4 text-amber-600 dark:text-amber-400" />
              </div>
              <span className="font-medium text-gray-900 dark:text-white">Ranking Vendedores</span>
            </div>
          </div>

          {/* Toggle Dia/Mês */}
          <div className="flex items-center justify-between mb-4 gap-2">
            <div className="flex bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
              <button
                onClick={() => setViewMode("day")}
                className={cn(
                  "px-3 py-1 text-xs font-medium rounded-md transition-all",
                  viewMode === "day"
                    ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm"
                    : "text-gray-500 dark:text-gray-400 hover:text-gray-700"
                )}
              >
                Dia
              </button>
              <button
                onClick={() => setViewMode("month")}
                className={cn(
                  "px-3 py-1 text-xs font-medium rounded-md transition-all",
                  viewMode === "month"
                    ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm"
                    : "text-gray-500 dark:text-gray-400 hover:text-gray-700"
                )}
              >
                Mês
              </button>
            </div>

            {/* Filtros */}
            <div className="flex items-center gap-1">
              {/* Filtro de meta batida */}
              <button
                onClick={() => {
                  setFilterHitGoal(!filterHitGoal);
                  if (!filterHitGoal) setFilterOnlyMe(false);
                }}
                className={cn(
                  "flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md transition-all",
                  filterHitGoal
                    ? "bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400"
                    : "bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-200"
                )}
              >
                <Filter className="h-3 w-3" />
                {goalLabel} ({hitGoalCount})
              </button>

              {/* Filtro "Me encontrar" - apenas para vendedores */}
              {highlightedSellerId && (
                <button
                  onClick={() => {
                    setFilterOnlyMe(!filterOnlyMe);
                    if (!filterOnlyMe) setFilterHitGoal(false);
                  }}
                  className={cn(
                    "flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md transition-all",
                    filterOnlyMe
                      ? "bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400"
                      : "bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-200"
                  )}
                >
                  <User className="h-3 w-3" />
                  {currentSellerPosition ? `${currentSellerPosition}º` : "Eu"}
                </button>
              )}
            </div>
          </div>

          {ranking.length > 0 ? (
            <div className="space-y-2 max-h-[280px] overflow-y-auto">
              {ranking.map((seller, index) => {
                // When "only me" filter is active, show real position from full ranking
                const realPos = filterOnlyMe && currentSellerPosition
                  ? currentSellerPosition - 1
                  : index;
                return renderSellerItem(seller, index, realPos);
              })}
            </div>
          ) : (
            <div className="text-center py-6">
              <div className="inline-flex items-center justify-center w-10 h-10 bg-gray-100 dark:bg-gray-800 rounded-full mb-2">
                <Trophy className="h-5 w-5 text-gray-400" />
              </div>
              <p className="text-xs text-gray-500">
                {filterHitGoal
                  ? `Nenhum vendedor bateu a ${goalLabel.toLowerCase()}`
                  : "Nenhuma venda no período"
                }
              </p>
            </div>
          )}

          {totalSellers > 10 && (
            <button
              onClick={() => setIsModalOpen(true)}
              className="w-full text-xs text-amber-600 dark:text-amber-400 hover:text-amber-700 text-center mt-3 pt-3 border-t border-gray-100 dark:border-gray-800 hover:underline flex items-center justify-center gap-1"
            >
              <Users className="h-3 w-3" />
              Ver todos os {totalSellers} vendedores
            </button>
          )}
        </CardContent>
      </Card>

      {/* Modal com todos os vendedores */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] p-0 gap-0 overflow-hidden">
          {/* Header com gradiente */}
          <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-6 text-white">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3 text-white text-xl">
                <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                  <Trophy className="h-6 w-6" />
                </div>
                Ranking Completo - {viewMode === "day" ? "Dia" : "Mês"}
              </DialogTitle>
            </DialogHeader>

            {/* Controles no header */}
            <div className="flex items-center justify-between gap-3 mt-4">
              <div className="flex bg-white/20 backdrop-blur-sm rounded-lg p-1">
                <button
                  onClick={() => setViewMode("day")}
                  className={cn(
                    "px-4 py-2 text-sm font-medium rounded-md transition-all",
                    viewMode === "day"
                      ? "bg-white text-amber-600 shadow-sm"
                      : "text-white/80 hover:text-white hover:bg-white/10"
                  )}
                >
                  Dia
                </button>
                <button
                  onClick={() => setViewMode("month")}
                  className={cn(
                    "px-4 py-2 text-sm font-medium rounded-md transition-all",
                    viewMode === "month"
                      ? "bg-white text-amber-600 shadow-sm"
                      : "text-white/80 hover:text-white hover:bg-white/10"
                  )}
                >
                  Mês
                </button>
              </div>

              <button
                onClick={() => setFilterHitGoal(!filterHitGoal)}
                className={cn(
                  "flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all",
                  filterHitGoal
                    ? "bg-white text-green-600 shadow-sm"
                    : "bg-white/20 backdrop-blur-sm text-white hover:bg-white/30"
                )}
              >
                <Filter className="h-4 w-4" />
                {goalLabel} batida ({hitGoalCount})
              </button>

              <Button
                onClick={handleExportPdf}
                disabled={isExporting}
                variant="outline"
                size="sm"
                className="bg-white/20 backdrop-blur-sm text-white border-white/30 hover:bg-white hover:text-amber-600"
              >
                <Download className="h-4 w-4 mr-2" />
                {isExporting ? "Exportando..." : "Exportar PDF"}
              </Button>
            </div>
          </div>

          {/* Lista de vendedores */}
          <ScrollArea className="h-[55vh] p-4">
            {filteredAllRanking.length > 0 ? (
              <div className="space-y-3">
                {filteredAllRanking.map((seller, index) => renderModalSellerItem(seller, index))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full mb-4">
                  <Trophy className="h-8 w-8 text-gray-400" />
                </div>
                <p className="text-lg font-medium text-gray-600 dark:text-gray-400">
                  {filterHitGoal
                    ? `Nenhum vendedor bateu a ${goalLabel.toLowerCase()} ainda`
                    : "Nenhuma venda no período"
                  }
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">
                  {filterHitGoal && "Desative o filtro para ver todos os vendedores"}
                </p>
              </div>
            )}
          </ScrollArea>

          {/* Footer com resumo */}
          <div className="border-t bg-gray-50 dark:bg-gray-800/50 p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-gray-500" />
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    <strong>{filteredAllRanking.length}</strong> vendedor{filteredAllRanking.length !== 1 ? "es" : ""}
                  </span>
                </div>
                {hitGoalCount > 0 && (
                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-green-500" />
                    <span className="text-sm text-green-600 dark:text-green-400">
                      <strong>{hitGoalCount}</strong> bateram a {goalLabel.toLowerCase()}
                    </span>
                  </div>
                )}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                {viewMode === "day" ? "Vendas de hoje" : "Vendas do mês"}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
